import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CitationWithSource } from '@/hooks/useProjectCitations';
import { Chapter, Section } from '@/hooks/useChapters';

interface CitationEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  citation: CitationWithSource;
  chapters: Chapter[];
  sections: Record<string, Section[]>;
  onSave: (
    citationId: string,
    updates: {
      section_id?: string | null;
      citation_text?: string | null;
      page_reference?: string | null;
      is_direct_quote?: boolean;
    }
  ) => Promise<void>;
}

export function CitationEditDialog({
  isOpen,
  onClose,
  citation,
  chapters,
  sections,
  onSave,
}: CitationEditDialogProps) {
  const [sectionId, setSectionId] = useState<string | null>(citation.section_id);
  const [citationText, setCitationText] = useState(citation.citation_text || '');
  const [pageReference, setPageReference] = useState(citation.page_reference || '');
  const [isDirectQuote, setIsDirectQuote] = useState(citation.is_direct_quote || false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSectionId(citation.section_id);
    setCitationText(citation.citation_text || '');
    setPageReference(citation.page_reference || '');
    setIsDirectQuote(citation.is_direct_quote || false);
  }, [citation]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(citation.id, {
        section_id: sectionId,
        citation_text: citationText || null,
        page_reference: pageReference || null,
        is_direct_quote: isDirectQuote,
      });
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  // Build flat list of sections with chapter context
  const sectionOptions: { id: string; label: string; chapterTitle: string }[] = [];
  chapters.forEach((chapter) => {
    const chapterSections = sections[chapter.id] || [];
    chapterSections.forEach((section) => {
      sectionOptions.push({
        id: section.id,
        label: section.title,
        chapterTitle: chapter.title,
      });
    });
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Citation</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Source Info (read-only) */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-sm font-medium">
              {citation.research_item.source_author || citation.research_item.title}
            </p>
            {citation.research_item.source_author && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {citation.research_item.title}
              </p>
            )}
          </div>
          
          {/* Section Assignment */}
          <div className="space-y-2">
            <Label>Assign to Section</Label>
            <Select
              value={sectionId || 'unassigned'}
              onValueChange={(value) => setSectionId(value === 'unassigned' ? null : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a section" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">
                  <span className="text-muted-foreground">Unassigned</span>
                </SelectItem>
                {sectionOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    <span className="text-muted-foreground">{option.chapterTitle} &rsaquo;</span>{' '}
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Citation Text */}
          <div className="space-y-2">
            <Label htmlFor="citation-text">Citation Text (optional)</Label>
            <Textarea
              id="citation-text"
              value={citationText}
              onChange={(e) => setCitationText(e.target.value)}
              placeholder="Enter the cited text or summary..."
              className="min-h-[80px]"
            />
          </div>
          
          {/* Page Reference */}
          <div className="space-y-2">
            <Label htmlFor="page-ref">Page Reference (optional)</Label>
            <Input
              id="page-ref"
              value={pageReference}
              onChange={(e) => setPageReference(e.target.value)}
              placeholder="e.g., 42-45"
            />
          </div>
          
          {/* Direct Quote Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="direct-quote"
              checked={isDirectQuote}
              onCheckedChange={(checked) => setIsDirectQuote(checked === true)}
            />
            <Label htmlFor="direct-quote" className="text-sm font-normal">
              This is a direct quote
            </Label>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
